import React, { createContext, useContext, useCallback, useState } from "react";
import { MobileAppVersionData, MOBILE_APP_VERSION_API_ENDPOINTS, defaultMobileAppVersionData } from "./constants";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../../utils/axiosInstance";
import { message } from "antd";

interface MobileAppVersionContextState {
	mobileAppVersionData: MobileAppVersionData;
	loadingMobileAppVersionData?: boolean;
	loadingAddMobileAppVersionData: boolean;
	loadingGetMobileAppVersionData?: boolean;
	loading: boolean;
	loadingEditMobileAppVersionData: boolean;
	setMobileAppVersionData: React.Dispatch<React.SetStateAction<MobileAppVersionData>>;
	addMobileAppVersion: () => void;
	updateMobileAppVersion: () => void;
	getMobileAppVersionDetails: (id: string) => void;
	clearMobileAppVersionData: () => void;
}

interface MobileAppVersionProviderProps {
	children: React.ReactNode;
}

const MobileAppVersionContext = createContext<MobileAppVersionContextState | undefined>(
	undefined
);

export const useMobileAppVersionContext = () => {
	const context = useContext(MobileAppVersionContext);
	if (!context) {
		throw new Error(
			"useMobileAppVersionContext must be used within a MobileAppVersionContextProvider"
		);
	}
	return context;
};

export const MobileAppVersionContextProvider: React.FC<MobileAppVersionProviderProps> = ({
	children,
}) => {
	const navigate = useNavigate();
	const [mobileAppVersionData, setMobileAppVersionData] = useState<MobileAppVersionData>(defaultMobileAppVersionData);

	const [loadingAddMobileAppVersionData, setLoadingAddMobileAppVersionData] = useState(false);
	const [loadingEditMobileAppVersionData, setLoadingEditMobileAppVersionData] = useState(false);
	const [loadingGetMobileAppVersionData, setLoadingGetMobileAppVersionData] = useState(false);
	const [errorMobileAppVersionData, setErrorMobileAppVersionData] = useState<any>(undefined);

	const loading = loadingAddMobileAppVersionData;
	const error = errorMobileAppVersionData;

	const COMPLETE_VERSION_REGEX = /^\d+\.\d+\.\d+$/;

	const isVersionValid = (version: string): boolean => {
		return COMPLETE_VERSION_REGEX.test(version);
	};

	const isMobileAppVersionDataValid = (data: MobileAppVersionData) => {
		if (!data.platform || !data.version || data.hasWarning === undefined || data.forceUpdate === undefined || !data.appId) {
			return false;
		}

		if (!isVersionValid(data.version)) {
			return false;
		}

		return true;
	};

	const clearMobileAppVersionData = useCallback(() => {
		setMobileAppVersionData(defaultMobileAppVersionData);
	}, []); 

	const addMobileAppVersion = async () => {
		setLoadingAddMobileAppVersionData(true);

		if (!isMobileAppVersionDataValid(mobileAppVersionData)) {
			if (!isVersionValid(mobileAppVersionData.version)) {
				message.info("Sürüm formatı X.Y.Z şeklinde olmalıdır (Örn: 1.0.0)");
			} else {
				message.info("Lütfen tüm zorunlu alanları doldurun.");
			}
			setLoadingAddMobileAppVersionData(false);
			return;
		}

		const payload = {
			platform: mobileAppVersionData.platform,
			version: mobileAppVersionData.version,
			hasWarning: mobileAppVersionData.hasWarning,
			forceUpdate: mobileAppVersionData.forceUpdate,
			appId: mobileAppVersionData.appId,
		};

		try {
			const response = await axiosInstance.post(`${MOBILE_APP_VERSION_API_ENDPOINTS.ADD_MOBILE_APP_VERSION}`, payload);

			setLoadingAddMobileAppVersionData(false);

			if (response.data && response.data.result?.isSuccessful) {
				message.success("Mobil Uygulama Sürümü başarıyla eklendi.");
				setMobileAppVersionData(defaultMobileAppVersionData);
				navigate("/mobile-app-versions");
			} else {
				message.error(response.data.result?.error?.message || "Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			setErrorMobileAppVersionData(error);
			setLoadingAddMobileAppVersionData(false);
		}
	};

	const updateMobileAppVersion = async () => {
		setLoadingEditMobileAppVersionData(true);

		if (!isMobileAppVersionDataValid(mobileAppVersionData)) {
			if (!isVersionValid(mobileAppVersionData.version)) {
				message.info("Sürüm formatı X.Y.Z şeklinde olmalıdır (Örn: 1.0.0)");
			} else {
				message.info("Lütfen tüm zorunlu alanları doldurun.");
			}
			setLoadingEditMobileAppVersionData(false);
			return;
		}

		const payload = {
			versionId: mobileAppVersionData.versionId,
			platform: mobileAppVersionData.platform,
			version: mobileAppVersionData.version,
			hasWarning: mobileAppVersionData.hasWarning,
			forceUpdate: mobileAppVersionData.forceUpdate,
			appId: mobileAppVersionData.appId
		};

		try {
			const response = await axiosInstance.put(`${MOBILE_APP_VERSION_API_ENDPOINTS.UPDATE_MOBILE_APP_VERSION}`, payload);

			setLoadingEditMobileAppVersionData(false);

			if (response.data && response.data.result?.isSuccessful) {
				message.success("Mobil Uygulama Sürümü başarıyla güncellendi.");
				setMobileAppVersionData(defaultMobileAppVersionData);
				navigate("/mobile-app-versions");
			} else {
				message.error(response.data.result?.error?.message || "Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			setErrorMobileAppVersionData(error);
			setLoadingEditMobileAppVersionData(false);
		}
	};

	const getMobileAppVersionDetails = async (id: string) => { 
		setLoadingGetMobileAppVersionData(true);
		try {
			const response = await axiosInstance.get(`${MOBILE_APP_VERSION_API_ENDPOINTS.GET_MOBILE_APP_VERSION}?versionId=${id}`);
			if (response.data && response.data.isSuccessful) {
				setMobileAppVersionData(response.data?.result);
			}
			setLoadingGetMobileAppVersionData(false);
		} catch (error: any) {
			console.error("getMobileAppVersionDetails error:", error);
			setErrorMobileAppVersionData(error);
			setLoadingGetMobileAppVersionData(false);
		}
	};

	const value: MobileAppVersionContextState = {
		mobileAppVersionData,
		loadingGetMobileAppVersionData,
		loadingAddMobileAppVersionData,
		loadingEditMobileAppVersionData,
		loading,
		setMobileAppVersionData,
		addMobileAppVersion,
		clearMobileAppVersionData,
		updateMobileAppVersion,
		getMobileAppVersionDetails,
	};

	return (
		<MobileAppVersionContext.Provider value={value}>
			{children}
		</MobileAppVersionContext.Provider>
	);
};